import React, { useEffect, useState } from 'react';
import { Trophy, Star, Sparkles } from 'lucide-react';

interface CelebrationProps {
  show: boolean;
  title?: string;
  message?: string;
}

export function Celebration({ show, title = 'Awesome!', message = 'Great job!' }: CelebrationProps) {
  const [confetti, setConfetti] = useState<Array<{ id: number; x: number; delay: number; duration: number }>>([]);

  useEffect(() => {
    if (show) {
      // Generate confetti pieces
      const pieces = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 2,
      }));
      setConfetti(pieces);
    }
  }, [show]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
      {/* Confetti */}
      {confetti.map((piece) => (
        <div
          key={piece.id}
          className="absolute top-0 w-2 h-2 rounded-full animate-confetti"
          style={{
            left: `${piece.x}%`,
            backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'][
              piece.id % 6
            ],
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
          }}
        />
      ))}

      {/* Main celebration card */}
      <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md mx-4 text-center animate-bounceIn">
        <div className="mb-6">
          <div className="flex justify-center gap-4 mb-4">
            <Trophy className="text-yellow-500" size={48} />
            <Star className="text-yellow-500 fill-yellow-500" size={48} />
            <Sparkles className="text-yellow-500" size={48} />
          </div>
        </div>

        <h2 className="text-5xl font-bold text-primary mb-4">{title}</h2>
        <p className="text-2xl text-gray-600 mb-6">{message}</p>

        <div className="text-6xl mb-4 animate-bounce">🎉</div>

        <div className="flex justify-center gap-2">
          {['⭐', '🏆', '🎯', '✨', '🌟'].map((emoji, idx) => (
            <span
              key={idx}
              className="text-3xl animate-pulse"
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              {emoji}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
